import { PartFinderFeedbackRating } from '@/api/api';
import { useClearPartFinderFeedback } from '@/hooks/api/part-finder-reports/useClearPartFinderFeedback';
import { usePartFinderFeedback } from '@/hooks/api/part-finder-reports/usePartFinderFeedback';
import { useSetPartFinderFeedback } from '@/hooks/api/part-finder-reports/useSetPartFinderFeedback';
import { getApiError } from '@/types/apiError';
import {
  ChatBubbleOutlineOutlined,
  ThumbDownAlt,
  ThumbDownAltOutlined,
  ThumbUpAlt,
  ThumbUpAltOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

const Row = styled(Box, { shouldForwardProp: property => property !== 'align' })<{ align: 'start' | 'center' }>(
  ({ align }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'center' ? 'center' : 'flex-start',
    gap: '0px',
  })
);

const Prompt = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8125rem',
  marginRight: '4px',
}));

const FeedbackButton = styled(IconButton)({
  padding: '3px',
});

const NotePopoverBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  width: '300px',
});

const NOTE_MAX_LENGTH = 2000;
const ICON_SIZE = 15;

const RATING_OPTIONS: {
  rating: PartFinderFeedbackRating;
  label: string;
  Filled: typeof ThumbUpAlt;
  Outlined: typeof ThumbUpAlt;
}[] = [
  { rating: 'up', label: 'Helpful', Filled: ThumbUpAlt, Outlined: ThumbUpAltOutlined },
  { rating: 'down', label: 'Not helpful', Filled: ThumbDownAlt, Outlined: ThumbDownAltOutlined },
];

export const PartFinderFeedbackControl = ({
  reportId,
  targetId,
  promptText,
  align = 'start',
}: {
  reportId: string;
  targetId: string;
  promptText?: string;
  align?: 'start' | 'center';
}) => {
  const { data: feedbackList } = usePartFinderFeedback(reportId);
  const setMutation = useSetPartFinderFeedback(reportId);
  const clearMutation = useClearPartFinderFeedback(reportId);

  const current = feedbackList?.find(feedback => feedback.targetId === targetId);
  const currentRating = current?.rating;
  const savedNote = current?.note ?? '';

  const noteButtonReference = useRef<HTMLButtonElement>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(savedNote);

  useEffect(() => {
    setNote(savedNote);
  }, [savedNote]);

  const isPending = setMutation.isPending || clearMutation.isPending;
  const activeError = setMutation.error ?? clearMutation.error;
  const apiError = activeError ? getApiError(activeError, 'Failed to save feedback') : undefined;

  const resetMutations = () => {
    setMutation.reset();
    clearMutation.reset();
  };

  const applyRating = (rating: PartFinderFeedbackRating) => {
    if (isPending) return;
    resetMutations();
    if (currentRating === rating) {
      clearMutation.mutate(targetId);
      setNoteOpen(false);
      return;
    }
    setMutation.mutate({ targetId, rating, note: note.trim() || undefined });
  };

  const handleSaveNote = () => {
    if (!currentRating || isPending) return;
    resetMutations();
    setMutation.mutate({ targetId, rating: currentRating, note: note.trim() || undefined });
    setNoteOpen(false);
  };

  const handleCloseNote = () => {
    setNote(savedNote);
    setNoteOpen(false);
  };

  return (
    <Box>
      <Row align={align}>
        {promptText && <Prompt>{promptText}</Prompt>}
        {RATING_OPTIONS.map(({ rating, label, Filled, Outlined }) => {
          const selected = currentRating === rating;
          const Icon = selected ? Filled : Outlined;
          return (
            <Tooltip key={rating} title={label}>
              <span>
                <FeedbackButton
                  size='small'
                  color={selected ? 'primary' : 'default'}
                  onClick={() => applyRating(rating)}
                  disabled={isPending}
                  aria-label={`Mark ${label.toLowerCase()}`}
                  aria-pressed={selected}
                  sx={{ color: selected ? undefined : 'text.disabled' }}
                >
                  <Icon sx={{ fontSize: ICON_SIZE }} />
                </FeedbackButton>
              </span>
            </Tooltip>
          );
        })}
        {currentRating && (
          <Tooltip title={savedNote ? 'Edit note' : 'Add a note'}>
            <span>
              <FeedbackButton
                ref={noteButtonReference}
                size='small'
                color={savedNote ? 'primary' : 'default'}
                onClick={() => setNoteOpen(true)}
                disabled={isPending}
                aria-label={savedNote ? 'Edit feedback note' : 'Add feedback note'}
                sx={{ color: savedNote ? undefined : 'text.disabled' }}
              >
                <ChatBubbleOutlineOutlined sx={{ fontSize: ICON_SIZE }} />
              </FeedbackButton>
            </span>
          </Tooltip>
        )}
        {isPending && <CircularProgress size={12} />}
      </Row>
      <Popover
        open={noteOpen}
        anchorEl={noteButtonReference.current}
        onClose={handleCloseNote}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <NotePopoverBody>
          <TextField
            value={note}
            onChange={event => setNote(event.target.value)}
            placeholder='Why was this helpful or not?'
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            autoFocus
            size='small'
            disabled={isPending}
            inputProps={{ 'aria-label': 'Feedback note', maxLength: NOTE_MAX_LENGTH }}
          />
          {apiError && <Alert severity='error'>{apiError.message}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button size='small' onClick={handleCloseNote} disabled={isPending}>
              Cancel
            </Button>
            <Button size='small' variant='contained' onClick={handleSaveNote} disabled={isPending}>
              Save
            </Button>
          </Box>
        </NotePopoverBody>
      </Popover>
    </Box>
  );
};
